import {
  faArrowUpRightFromSquare,
  faCheck,
  faEllipsisVertical,
  faFileLines,
  faLock,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import ContractCoverageProfileCard from '@/components/ContractCoverageProfileCard';
import ContractDetailSkeleton from '@/components/ContractDetailSkeleton';
import ContractEntityFilters from '@/components/ContractEntityFilters';
import type { ContractEntityFilterValues } from '@/components/ContractEntityFilters/types';
import ContractVersionChanges from '@/components/ContractVersionChanges';
import { CONTRACTS_PATH } from '@/constants/contracts';
import { DRUG_CATEGORIES } from '@/constants/programs';
import AppContext from '@/contexts/appContext';
import {
  useDugoutContractDetail,
  useDugoutContractVersionHistory
} from '@/hooks/useDugoutContracts';
import useDugoutActiveProgramFamilies from '@/hooks/useDugoutProgramFamilies';
import type { ContractActiveProgram, ContractInsuranceEntityDetail } from '@/types/contract';
import {
  Button,
  buttonVariants,
  Card,
  Pill,
  PillSwitch,
  PillSwitchOption,
  Spinner,
  Timeline,
  TimelineItem,
  Typography
} from '@/ui';
import Container from '@/ui/components/Container';
import type { OptionType } from '@/ui/components/Form/Select/types';
import NotAuthorized from '@/ui/components/NotAuthorized';
import {
  familyColumnLabel,
  formatContractDate,
  formatContractMonth,
  PHASE_TYPE_LEGEND
} from '@/utils/contractLabels';
import { CONTRACT_VIEW, hasPermission } from '@/utils/userPermission';

// Versions shown in the history before "More versions", and how many each click adds.
const VERSION_PAGE_SIZE = 4;
const term = (start: string, end: string | null) =>
  `${formatContractMonth(start)} → ${end ? formatContractMonth(end) : 'open'}`;
const matches = (selected: OptionType<string>[], value: string) =>
  !selected.length || selected.some((option) => option.value === value);

const profileLabel = (index: number) =>
  index < 26 ? String.fromCharCode(65 + index) : String(index + 1);

// Entities contracting the exact same programs share a coverage profile, largest first.
const groupByCoverage = (entities: ContractInsuranceEntityDetail[]) => {
  const profiles = new Map<string, ContractInsuranceEntityDetail[]>();
  entities.forEach((entity) => {
    const key = [...entity.program_ids].sort().join(',');
    profiles.set(key, [...(profiles.get(key) ?? []), entity]);
  });
  return [...profiles.values()].sort(
    (a, b) => b.length - a.length || b[0].program_ids.length - a[0].program_ids.length
  );
};

const ContractDetailContent = () => {
  const { contractId = '' } = useParams();
  const [searchParams] = useSearchParams();
  // The URL's contract is the latest version; ?version= shows an older one in its place.
  const {
    data: versionHistory,
    isPending: historyPending,
    isError: historyError
  } = useDugoutContractVersionHistory(contractId);
  const selectedId = searchParams.get('version') ?? contractId;
  const {
    data: contract,
    isPending,
    isError,
    isPlaceholderData: switchingVersion
  } = useDugoutContractDetail(selectedId);
  const {
    data: families = [],
    isPending: familiesPending,
    isError: familiesError
  } = useDugoutActiveProgramFamilies();
  const [gapsOnly, setGapsOnly] = useState(false);
  const [filters, setFilters] = useState<ContractEntityFilterValues>({
    providers: [],
    types: [],
    jurisdictions: []
  });
  const [shownVersionCount, setShownVersionCount] = useState(VERSION_PAGE_SIZE);
  // Coverage needs the program families too, so an empty table never stands in for missing data.
  if (isPending || historyPending || familiesPending) return <ContractDetailSkeleton />;
  if (isError || historyError || familiesError || !contract || !versionHistory)
    return <Typography>Unable to load this contract.</Typography>;

  const versionCount = versionHistory.length;
  // Always reach far enough down the history to include the version being viewed.
  const selectedIndex = versionHistory.findIndex((version) => version.id === selectedId);
  const shownVersions = versionHistory.slice(0, Math.max(shownVersionCount, selectedIndex + 1));
  const hiddenVersionCount = versionCount - shownVersions.length;
  const profiles = groupByCoverage(contract.insurance_entities);
  const programIds = (entity: ContractInsuranceEntityDetail) => new Set(entity.program_ids);
  const hasGap = (entity: ContractInsuranceEntityDetail, programs: ContractActiveProgram[]) => {
    const ids = programIds(entity);
    return programs.some((program) => !ids.has(program.id));
  };
  const isFiltering =
    filters.providers.length + filters.types.length + filters.jurisdictions.length > 0;
  const matchesFilters = (entity: ContractInsuranceEntityDetail) =>
    matches(filters.providers, entity.insurance_provider_id) &&
    matches(filters.types, entity.insurance_type) &&
    matches(filters.jurisdictions, entity.jurisdiction_id);
  // Labels stay tied to the full grouping so a profile keeps its letter while filtering.
  const visibleProfiles = profiles
    .map((entities, index) => ({
      label: profileLabel(index),
      entities: entities.filter(matchesFilters)
    }))
    .filter(
      ({ entities }) =>
        entities.length > 0 && (!gapsOnly || hasGap(entities[0], contract.active_programs))
    );
  const matchingEntityCount = visibleProfiles.reduce(
    (count, { entities }) => count + entities.length,
    0
  );
  const currentVersion = versionHistory.find((version) => version.id === contract.id);
  const newEntityIds = new Set(
    currentVersion && currentVersion.version > 1
      ? currentVersion.added.map((entity) => entity.id)
      : []
  );
  const statusColor =
    contract.status === 'active'
      ? 'primary'
      : contract.status === 'upcoming'
        ? 'secondary'
        : 'neutral';

  const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

  return (
    <Container type="full-width-page">
      <Typography variant="caption" as="nav" muted className="mb-4">
        <Link to={CONTRACTS_PATH} className="hover:underline">
          Contracts
        </Link>{' '}
        / {contract.name}
      </Typography>
      <Typography variant="title" as="h1">
        {contract.name}{' '}
        {contract.locked_at && (
          <FontAwesomeIcon icon={faLock} title="Locked" className="text-base" />
        )}
      </Typography>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Pill size="sm" variant="subtle" color={statusColor}>
          {contract.status[0].toUpperCase() + contract.status.slice(1)}
        </Pill>
        <Typography as="span" muted>
          Version {contract.version} of {versionCount}
        </Typography>
        <Typography as="span" muted>
          {term(contract.start_at, contract.end_at)}
        </Typography>
        <Typography as="span" muted>
          {pluralize(contract.insurance_entity_count, 'insurance entity', 'insurance entities')}
        </Typography>
      </div>
      <div className="mt-6 grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <main className="relative min-w-0 space-y-5" aria-busy={switchingVersion}>
          {switchingVersion && (
            <div className="absolute inset-0 z-10 flex justify-center bg-white/60 pt-24 dark:bg-uniform-gray-900/60">
              <Spinner size="lg" color="primary" label="Loading version" />
            </div>
          )}
          <Card padding="none" className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-uniform-gray-200 p-4 dark:border-uniform-gray-700">
              <Typography variant="heading" as="h2">
                Program coverage
              </Typography>
              <Typography muted>
                Insurance entities covering each program family (active programs only)
              </Typography>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
              {DRUG_CATEGORIES.map((category) => (
                <div
                  key={category.value}
                  className="border-b border-uniform-gray-200 p-4 last:border-b-0 sm:odd:border-r lg:border-b-0 lg:border-r lg:last:border-r-0 dark:border-uniform-gray-700">
                  <Typography variant="label" as="h3" muted className="mb-2">
                    {category.label}
                  </Typography>
                  <div className="space-y-1">
                    {(contract.category_coverage[category.value]?.families ?? []).map((family) => (
                      <div key={family.program_family_id} className="flex justify-between gap-2">
                        <Typography as="span">
                          {familyColumnLabel({
                            id: family.program_family_id,
                            name: family.program_family_name,
                            drug_category: category.value,
                            treatment_preference: family.treatment_preference
                          })}
                        </Typography>
                        <Typography as="span">
                          {family.fully_covered_insurance_entities}/
                          {contract.insurance_entity_count}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
          {currentVersion && currentVersion.version > 1 && (
            <ContractVersionChanges version={currentVersion} />
          )}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <Typography variant="heading" as="h2">
                  Contracted insurance entities ({contract.insurance_entity_count})
                </Typography>
                {isFiltering && (
                  <Typography muted>
                    Showing {matchingEntityCount} of {contract.insurance_entities.length}
                  </Typography>
                )}
                <Typography muted>
                  {pluralize(profiles.length, 'coverage profile', 'coverage profiles')}: entities
                  contracting the same active programs.
                </Typography>
              </div>
              <PillSwitch
                value={gapsOnly ? 'gaps' : 'all'}
                onChange={(value) => setGapsOnly(value === 'gaps')}
                size="sm">
                <PillSwitchOption value="all">All programs</PillSwitchOption>
                <PillSwitchOption value="gaps">Gaps only</PillSwitchOption>
              </PillSwitch>
            </div>
            <div className="mt-4">
              <ContractEntityFilters
                entities={contract.insurance_entities}
                values={filters}
                onChange={setFilters}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
              <Typography
                variant="caption"
                as="span"
                muted
                className="inline-flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faCheck}
                  aria-hidden="true"
                  className="text-uniform-green-700"
                />
                Contracted
              </Typography>
              <Typography
                variant="caption"
                as="span"
                muted
                className="inline-flex items-center gap-1">
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" className="text-orange-600" />
                Offered, not contracted
              </Typography>
              <Typography
                variant="caption"
                as="span"
                muted
                className="inline-flex items-center gap-1">
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 bg-uniform-gray-200 dark:bg-uniform-gray-700"
                />
                Not offered at that phase type
              </Typography>
              {newEntityIds.size > 0 && (
                <Typography
                  variant="caption"
                  as="span"
                  muted
                  className="inline-flex items-center gap-1">
                  <span
                    aria-hidden="true"
                    className="h-3.5 w-3.5 rounded border border-blue-600 dark:border-blue-400"
                  />
                  New in version {contract.version}
                </Typography>
              )}
            </div>
            <Typography variant="caption" muted className="mt-1">
              {PHASE_TYPE_LEGEND}
            </Typography>
            {visibleProfiles.length > 0 ? (
              <div className="mt-4 space-y-4">
                {visibleProfiles.map(({ label, entities }) => (
                  <ContractCoverageProfileCard
                    key={label}
                    label={label}
                    entities={entities}
                    activePrograms={contract.active_programs}
                    families={families}
                    gapsOnly={gapsOnly}
                    newEntityIds={newEntityIds}
                    version={contract.version}
                  />
                ))}
              </div>
            ) : (
              (isFiltering || gapsOnly) && (
                <Typography muted className="mt-4">
                  {isFiltering
                    ? 'No insurance entities match these filters.'
                    : 'No missing program combinations.'}
                </Typography>
              )
            )}
          </Card>
        </main>
        <aside className="space-y-4">
          <Card>
            <dl className="space-y-3">
              {[
                ['Organization', contract.organization_name ?? '—'],
                ['Term start', formatContractDate(contract.start_at)],
                ['Term end', contract.end_at ? formatContractDate(contract.end_at) : 'Open-ended'],
                [
                  'Editing',
                  contract.locked_at
                    ? `Locked ${new Date(contract.locked_at).toLocaleDateString()}`
                    : 'Unlocked'
                ]
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <Typography as="dt" muted>
                    {label}
                  </Typography>
                  <Typography as="dd" className="text-right">
                    {value}
                  </Typography>
                </div>
              ))}
              {contract.document && (
                <div className="flex items-center justify-between gap-4">
                  <Typography as="dt" muted>
                    Signed document
                  </Typography>
                  <dd>
                    <a
                      href={contract.document}
                      target="_blank"
                      rel="noreferrer"
                      className={buttonVariants({
                        variant: 'outline',
                        color: 'neutral',
                        size: 'sm'
                      })}>
                      <FontAwesomeIcon icon={faFileLines} />
                      View PDF
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </Card>
          <Card>
            <Typography variant="heading" as="h2">
              Version history
            </Typography>
            <Typography muted className="mb-4">
              Changes compared with the previous version.
            </Typography>
            <Timeline>
              {shownVersions.map((version) => {
                const programsAdded = version.changed.reduce(
                  (count, change) => count + change.added_programs.length,
                  0
                );
                const programsRemoved = version.changed.reduce(
                  (count, change) => count + change.removed_programs.length,
                  0
                );
                const versionLabel =
                  version.version === 1 ? 'Original' : `Version ${version.version}`;
                const summary =
                  version.version > 1
                    ? [
                        version.added.length &&
                          `+${pluralize(version.added.length, 'entity', 'entities')}`,
                        version.removed.length &&
                          `−${pluralize(version.removed.length, 'entity', 'entities')}`,
                        programsAdded && `+${pluralize(programsAdded, 'program', 'programs')}`,
                        programsRemoved && `−${pluralize(programsRemoved, 'program', 'programs')}`
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : pluralize(
                        version.insurance_entity_count,
                        'insurance entity',
                        'insurance entities'
                      );
                return (
                  <TimelineItem
                    key={version.id}
                    date={term(version.start_at, version.end_at)}
                    color={version.id === selectedId ? 'secondary' : 'neutral'}>
                    <Card
                      padding="sm"
                      className={
                        version.id === selectedId
                          ? 'border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-uniform-gray-800'
                          : undefined
                      }>
                      <Link
                        to={version.id === contractId ? '?' : `?version=${version.id}`}
                        aria-current={version.id === selectedId ? 'page' : undefined}
                        className="block hover:underline">
                        <Typography as="span" weight="semibold">
                          {versionLabel}
                        </Typography>{' '}
                        <Typography as="span" muted>
                          v{version.version} of {versionCount}
                        </Typography>
                        {version.id === contractId && (
                          <Pill size="sm" variant="subtle" color="primary" className="ml-2">
                            Current
                          </Pill>
                        )}
                        <Typography muted>{summary || 'No changes'}</Typography>
                      </Link>
                    </Card>
                  </TimelineItem>
                );
              })}
            </Timeline>
            {hiddenVersionCount > 0 && (
              // Sits under the timeline's dot column so the ellipsis continues the line.
              <div className="mt-3 flex items-center gap-4">
                <span className="flex w-4 justify-center text-uniform-gray-400">
                  <FontAwesomeIcon icon={faEllipsisVertical} aria-hidden="true" />
                </span>
                <Button
                  variant="quiet"
                  color="secondary"
                  size="sm"
                  onClick={() => setShownVersionCount(shownVersions.length + VERSION_PAGE_SIZE)}>
                  More versions ({hiddenVersionCount})
                </Button>
              </div>
            )}
          </Card>
        </aside>
      </div>
    </Container>
  );
};

const ContractDetail = () => {
  const { permissions } = useContext(AppContext);
  return hasPermission({ permissions, requiredPermission: CONTRACT_VIEW }) ? (
    <ContractDetailContent />
  ) : (
    <NotAuthorized />
  );
};

export default ContractDetail;
